import { useEffect, useState } from "react";
import { Avatar } from "@mui/material";
import BaseModal from "../../../components/ui/BaseModal";
import BaseButton from "../../../components/ui/BaseButton";
import { useMutation } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";
import BaseCard from "../../../components/ui/BaseCard";
import BaseRating from "../../../components/ui/BaseRating";
import BaseSwitch from "../../../components/ui/BaseSwitch";
import BaseSelect from "../../../components/ui/BaseSelect";
import {
  createClientFeedbackGivenByEngineer,
  createEngineerFeedbackGivenByClient,
} from "../../../services/api/feedback";
import BaseCheckbox from "../../../components/ui/BaseCheckbox";
import { UserType } from "../../../utils/enum";
import { useUpdateWorkOrder } from "../../../hooks/useUpdateWorkOrder";
import { setFeedbackModalOpen } from "../../../store/modalsManagement/modalsManagementSlice";
import UserProfileLink from "../../../components/ui/UserProfileLink";

const ClientToEngineerLikeMostTexts = [
  { text: "Excellent Technical Skills", value: "EXCELLENT_TECHNICAL_SKILLS", isSelected: false },
  { text: "Professional & Courteous", value: "PROFESSIONAL_AND_COURTEOUS", isSelected: false },
  { text: "On-Time Arrival", value: "ON_TIME_ARRIVAL", isSelected: false },
  { text: "Great Communication", value: "GREAT_COMMUNICATION", isSelected: false },
  { text: "High-Quality Work", value: "HIGH_QUALITY_WORK", isSelected: false },
  { text: "Went Above & Beyond", value: "WENT_ABOVE_AND_BEYOND", isSelected: false },
];

const ClientToEngineerProblemTexts = [
  { text: "Late Arrival", value: "LATE_ARRIVAL", isSelected: false },
  { text: "Poor Communication", value: "POOR_COMMUNICATION", isSelected: false },
  {
    text: "Insufficient Technical Skills",
    value: "INSUFFICIENT_TECHNICAL_SKILLS",
    isSelected: false,
  },
  { text: "Unprofessional Conduct", value: "UNPROFESSIONAL_CONDUCT", isSelected: false },
  {
    text: "Incomplete/Poor-Quality Work",
    value: "INCOMPLETE_OR_POOR_QUALITY_WORK",
    isSelected: false,
  },
  { text: "Did Not Follow Instructions", value: "DID_NOT_FOLLOW_INSTRUCTIONS", isSelected: false },
];

// Options an ENGINEER selects when leaving feedback for the CLIENT / assignment.
const EngineerToClientLikeMostTexts = [
  { text: "Clear Work Instructions", value: "CLEAR_WORK_INSTRUCTIONS", isSelected: false },
  { text: "Excellent Communication", value: "EXCELLENT_COMMUNICATION", isSelected: false },
  { text: "Smooth Site Access", value: "SMOOTH_SITE_ACCESS", isSelected: false },
  { text: "Quick Response & Support", value: "QUICK_RESPONSE_AND_SUPPORT", isSelected: false },
  { text: "Professional & Respectful", value: "PROFESSIONAL_AND_RESPECTFUL", isSelected: false },
  { text: "Well-Organized Assignment", value: "WELL_ORGANIZED_ASSIGNMENT", isSelected: false },
];

const EngineerToClientProblemTexts = [
  { text: "Unclear Work Instructions", value: "UNCLEAR_WORK_INSTRUCTIONS", isSelected: false },
  { text: "Site Access Issues", value: "SITE_ACCESS_ISSUES", isSelected: false },
  { text: "Poor Communication", value: "POOR_COMMUNICATION", isSelected: false },
  {
    text: "Incorrect Work Order Info.",
    value: "INCORRECT_WORK_ORDER_INFORMATION",
    isSelected: false,
  },
  { text: "Lack of On-Site Support", value: "LACK_OF_ONSITE_SUPPORT", isSelected: false },
  { text: "Disrespectful", value: "DISRESPECTFUL", isSelected: false },
];

// Preset feedback comments shown to clients leaving feedback for an engineer.
// The exact line is both the option value and what gets stored in `comment`.
// Selecting a preset fills the (disabled) comment box; "Other" enables free text.
const FEEDBACK_PRESET_COMMENTS = [
  "Your dedication, attention to detail, and commitment to excellence have played an important role in delivering a successful outcome for our customer.",
  "We appreciate the high standard of work you deliver and the positive impact you make on every assignment. Thank you for representing Tech Domain with professionalism and helping us maintain exceptional service quality.",
  "Thank you for going above and beyond to deliver quality service. Your contribution helps create successful outcomes for our customers and strengthens the reputation of the Tech Domain engineer network.",
];

const ClientFeedbackComments = [
  ...FEEDBACK_PRESET_COMMENTS.map((text) => ({ value: text, label: text })),
  { value: "OTHER", label: "Other" },
];

const feedbackSnapshot = ({ rating, comment, isPrivate, liked, problems }) =>
  JSON.stringify({
    rating: rating ?? null,
    comment: comment ?? "",
    isPrivate: !!isPrivate,
    liked: [...liked].sort(),
    problems: [...problems].sort(),
  });

const EMPTY_FEEDBACK_SNAPSHOT = feedbackSnapshot({
  rating: null,
  comment: "",
  isPrivate: false,
  liked: [],
  problems: [],
});

const FeedbackModal = ({
  feedbackModalOpen,
  setFeedbackSuccessModalOpen,
  refetchUserAllowedAction,
  previewFeedback,
  authRole,
  isEdit,
}) => {
  const dispatch = useDispatch();
  const { updateWorkOrder } = useUpdateWorkOrder();
  const workOrderDetail = useSelector((state) => state.workOrder.workOrderDetails);
  const isClient = authRole === UserType.CLIENT;
  // Whoever is being rated is also who the header name opens. Engineers see the
  // client's company name, so that resolves to the person who raised the work order.
  const counterpartId = isClient ? workOrderDetail?.engineer?.id : workOrderDetail?.createdBy?.id;
  // Clients rate the engineer; engineers rate the client / assignment. Each
  // direction shows its own set of positive/negative options.
  const likeMostOptions = isClient ? ClientToEngineerLikeMostTexts : EngineerToClientLikeMostTexts;
  const problemOptions = isClient ? ClientToEngineerProblemTexts : EngineerToClientProblemTexts;
  const [isCommentAdded, setIsCommentAdded] = useState(false);
  const [problemFeedbacks, setProblemFeedbacks] = useState(problemOptions);
  const [mostLikedFeedbacks, setMostLikedFeedbacks] = useState(likeMostOptions);
  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState("");
  const [selectedCommentOption, setSelectedCommentOption] = useState("");
  const [isPrivateFeedback, setIsPrivateFeedback] = useState(false);
  const [initialSnapshot, setInitialSnapshot] = useState(EMPTY_FEEDBACK_SNAPSHOT);

  const {
    isPending: isEngineerFeedbackApiLoading,
    error: isEngineerFeedbackApiError,
    mutateAsync: createEngineerFeedbackGivenByClientMutation,
  } = useMutation({
    mutationFn: createEngineerFeedbackGivenByClient,
  });
  const {
    isPending: isClientFeedbackApiLoading,
    error: isClientFeedbackApiError,
    mutateAsync: createClientFeedbackGivenByEngineerMutation,
  } = useMutation({
    mutationFn: createClientFeedbackGivenByEngineer,
  });

  useEffect(() => {
    if (!isEdit) return;

    // The current user's own feedback is exposed as `feedbackGiven` on the work
    // order detail. likedOptions/problemOptions come back as display texts.
    const ownFeedback = workOrderDetail?.feedback?.feedbackGiven;
    const savedComment = ownFeedback?.comment ?? "";

    setRating(ownFeedback?.rating ?? null);
    setComment(savedComment);
    setIsCommentAdded(true);

    if (isClient) {
      const matchedPreset = ClientFeedbackComments.find(
        (option) => option.value !== "OTHER" && option.label === savedComment,
      );
      setSelectedCommentOption(savedComment ? (matchedPreset ? matchedPreset.value : "OTHER") : "");
    }

    const likedTexts = ownFeedback?.likedOptions ?? [];
    const problemTexts = ownFeedback?.problemOptions ?? [];
    setMostLikedFeedbacks((prev) =>
      prev.map((item) => ({ ...item, isSelected: likedTexts.includes(item.text) })),
    );
    setProblemFeedbacks((prev) =>
      prev.map((item) => ({
        ...item,
        isSelected: problemTexts.includes(item.text),
      })),
    );

    setInitialSnapshot(
      feedbackSnapshot({
        rating: ownFeedback?.rating ?? null,
        comment: savedComment,
        isPrivate: false,
        liked: likeMostOptions
          .filter((item) => likedTexts.includes(item.text))
          .map((item) => item.value),
        problems: problemOptions
          .filter((item) => problemTexts.includes(item.text))
          .map((item) => item.value),
      }),
    );
  }, [workOrderDetail, isEdit, isClient]);

  const selectedMostLiked = mostLikedFeedbacks
    .filter((feedback) => feedback.isSelected)
    .map((feedback) => feedback.value);
  const selectedProblemFaced = problemFeedbacks
    .filter((feedback) => feedback.isSelected)
    .map((feedback) => feedback.value);

  const isDirty =
    feedbackSnapshot({
      rating,
      comment,
      isPrivate: isPrivateFeedback,
      liked: selectedMostLiked,
      problems: selectedProblemFaced,
    }) !== initialSnapshot;

  const handleRatingChange = (e, newValue) => {
    setRating(newValue);
  };

  const handleCommentChange = (e) => {
    setComment(e.target.value);
  };

  const handleMostLikedFeedbackClick = (value) => {
    if (previewFeedback) return;

    setMostLikedFeedbacks((prevMostLiked) =>
      prevMostLiked.map((item) =>
        item.value === value ? { ...item, isSelected: !item.isSelected } : item,
      ),
    );
  };

  const handleProblemFacedFeedbackClick = (value) => {
    if (previewFeedback) return;

    setProblemFeedbacks((prevProblemFaced) =>
      prevProblemFaced.map((item) =>
        item.value === value ? { ...item, isSelected: !item.isSelected } : item,
      ),
    );
  };

  const handleCommentOptionChange = (e) => {
    const value = e.target.value;
    setSelectedCommentOption(value);
    if (value === "OTHER") {
      setComment("");
    } else {
      const selected = ClientFeedbackComments.find((option) => option.value === value);
      setComment(selected?.label ?? "");
    }
  };

  const handleSubmitButton = async () => {
    try {
      const payload = {
        rating,
        comment,
        problemFaced: selectedProblemFaced,
        mostLiked: selectedMostLiked,
        workOrderId: workOrderDetail.id,
        isPrivate: isPrivateFeedback,
      };
      const feedbackResponse = isClient
        ? await createEngineerFeedbackGivenByClientMutation(payload)
        : authRole === UserType.FREELANCER
          ? await createClientFeedbackGivenByEngineerMutation(payload)
          : null;

      setFeedbackSuccessModalOpen(true);
      dispatch(
        setFeedbackModalOpen({
          workOrderId: workOrderDetail.id,
          isOpen: false,
        }),
      );
      setRating(null);
      setComment("");
      setSelectedCommentOption("");
      setIsPrivateFeedback(false);
      refetchUserAllowedAction();
      updateWorkOrder();
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message || "Feedback | something went wrong",
          type: "error",
        }),
      );
    }
  };

  const handleCloseButton = async () => {
    dispatch(
      setFeedbackModalOpen({
        workOrderId: workOrderDetail.id,
        isOpen: false,
      }),
    );
  };

  return (
    <BaseModal
      open={feedbackModalOpen}
      close={() =>
        dispatch(
          setFeedbackModalOpen({
            workOrderId: workOrderDetail.id,
            isOpen: false,
          }),
        )
      }
      // Fixed height so the header/footer stay put and only the body scrolls.
      style={{ overflowY: "hidden", maxHeight: "90vh" }}
    >
      {/* Fixed header */}
      <div className="container px-12 pt-4">
        <div className="flex items-center border-bottom pb-1">
          <h5 className="text-dark">Feedback</h5>
          <i className="fa-solid fa-lock ml-2 text-dark"></i>
        </div>
      </div>

      <div className="container px-12 overflow-y-auto max-h-[calc(90vh-180px)] pt-4">
        <div className="flex items-center">
          <UserProfileLink
            as="div"
            userId={counterpartId}
            search="?direct=true"
            className="shrink-0"
            linkClassName="cursor-pointer"
          >
            <Avatar
              src={
                isClient
                  ? (workOrderDetail?.engineer?.profilePicture ?? null)
                  : (workOrderDetail?.createdBy?.company?.logo ?? null)
              }
              sx={{ width: 48, height: 48 }}
            />
          </UserProfileLink>
          <div className="ml-2">
            <h6 className="text-primary capitalize">{workOrderDetail?.title}</h6>
            <UserProfileLink
              userId={counterpartId}
              search="?direct=true"
              className="w-fit"
              linkClassName="cursor-pointer hover:text-primary hover:underline"
            >
              {isClient
                ? `${workOrderDetail?.engineer.firstName ?? ""} ${workOrderDetail?.engineer.lastName ?? ""}`
                : workOrderDetail?.createdBy?.company?.name}
            </UserProfileLink>
          </div>
        </div>

        {!previewFeedback && (
          <p className="mt-3">
            The purpose of private feedback is to foster transparency and mutual growth by providing
            constructive insights on the working relationship. It helps to understand areas for
            improvement, such as communication or resource provision, while acknowledging their
            strengths. This feedback aims to enhance collaboration and streamline future projects.
            Ultimately, it promotes a professional, respectful, and efficient partnership.
          </p>
        )}

        <BaseCard
          className={`  ${!previewFeedback && "p-4"} mt-5 shadow-md border border-zinc-100`}
          elevation={0}
        >
          {!previewFeedback && (
            <p className="text-sm font-normal ">
              {` How would you rate your ${isClient ? "engineer's" : "client's"} overall approach to ${isClient ? "working on " : "managing"} this
              work order?`}
              <span className="text-red-500 ml-1">*</span>
            </p>
          )}
          <div className="flex items-center justify-between mx-24 my-4">
            <p>Unsatisfied</p>
            <BaseRating
              size="large"
              readOnly={previewFeedback ? true : false}
              rating={rating}
              onChange={(event, newValue) => handleRatingChange(event, newValue)}
            />
            <p>Excellent</p>
          </div>

          {previewFeedback && comment && (
            <div className="mx-6 mb-4 bg-light rounded-md py-2 px-3">
              <p dangerouslySetInnerHTML={{ __html: comment }}></p>
            </div>
          )}
        </BaseCard>

        <BaseCard className="py-4 px-10 mt-7 shadow-md border border-zinc-100" elevation={0}>
          <p className="text-sm font-normal ">
            {previewFeedback ? "What did Buyer like most?" : "What did you like most?"}
          </p>
          <div className="grid grid-cols-3 gap-x-6 gap-y-3 mt-3">
            {mostLikedFeedbacks?.map((feedback, index) => (
              <FeedbackText
                previewFeedback={previewFeedback}
                text={feedback.text}
                value={feedback.value}
                isSelected={feedback.isSelected}
                key={index}
                onClick={handleMostLikedFeedbackClick}
              />
            ))}
          </div>
        </BaseCard>
        <BaseCard className="py-4 px-9 mt-7 shadow-md border border-zinc-100" elevation={0}>
          <p className="text-sm font-normal ">
            {previewFeedback ? "What Problem Buyer face?" : "What problem you face?"}
          </p>
          <div className="grid grid-cols-3 gap-x-6 gap-y-3 mt-3">
            {problemFeedbacks?.map((feedback, index) => (
              <FeedbackText
                previewFeedback={previewFeedback}
                text={feedback.text}
                value={feedback.value}
                isSelected={feedback.isSelected}
                key={index}
                onClick={handleProblemFacedFeedbackClick}
              />
            ))}
          </div>
        </BaseCard>

        {!previewFeedback && (
          <div className="mt-6">
            <div className="flex items-center">
              <h6 className="font-normal mr-3">Add a comment</h6>
              <BaseSwitch
                secondary
                checked={isCommentAdded}
                onChange={() => {
                  setIsCommentAdded(!isCommentAdded);
                }}
              />
            </div>

            {isCommentAdded && (
              <>
                {isClient && (
                  <div className="mt-2 h-10 w-full [&_.MuiInputBase-root]:!w-0 [&_.MuiInputBase-root]:!min-w-full [&_.MuiSelect-select]:!min-w-0 [&_.MuiSelect-select]:overflow-hidden [&_.MuiSelect-select]:text-ellipsis [&_.MuiSelect-select]:whitespace-nowrap">
                    <BaseSelect
                      labelShrink={false}
                      fullHeight={false}
                      className=""
                      placeholder="Select a comment"
                      isDefaultField={false}
                      required={true}
                      options={ClientFeedbackComments}
                      value={selectedCommentOption}
                      onChange={handleCommentOptionChange}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxWidth: 480,
                            "& .MuiMenuItem-root": {
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                            },
                          },
                        },
                      }}
                    />
                  </div>
                )}
                <textarea
                  readOnly={previewFeedback ? true : false}
                  disabled={isClient && selectedCommentOption !== "OTHER"}
                  name="instructions"
                  value={comment}
                  onChange={(e) => handleCommentChange(e)}
                  className="mt-4 bg-white border border-gray-100 w-full rounded-md h-32 resize-none p-4 mt-1 focus:outline-none focus:ring-1 focus:ring-orange-400 text-xs font-[300] shadow-md disabled:bg-zinc-100 disabled:cursor-not-allowed disabled:text-zinc-500"
                ></textarea>
              </>
            )}

            {authRole === UserType.CLIENT && (
              <BaseCheckbox
                checked={isPrivateFeedback}
                label="Make it a Private Feedback"
                onChange={(e) => setIsPrivateFeedback(e.target.checked)}
              />
            )}
          </div>
        )}
      </div>
      <div
        className="flex items-center justify-center bg-white py-6 px-5 mt-3"
        style={{
          boxShadow: "5px 0px 14px 5px rgba(0, 0, 0, 0.07)",
        }}
      >
        {!previewFeedback && (
          <BaseButton
            className="py-[5px] px-2 bg-[#E8E8E8] text-primary"
            fullWidth={false}
            onClick={handleCloseButton}
          >
            Cancel
          </BaseButton>
        )}
        {!previewFeedback && (
          <BaseButton
            disabled={isEngineerFeedbackApiLoading || !isDirty || !rating}
            isLoading={isEngineerFeedbackApiLoading}
            className="py-[5px] px-4 ml-4"
            color="primary"
            fullWidth={false}
            onClick={handleSubmitButton}
          >
            {isEdit ? "Update" : "Submit"}
          </BaseButton>
        )}

        {previewFeedback && (
          <BaseButton
            className="py-[5px] px-4 ml-4"
            color="primary"
            fullWidth={false}
            onClick={handleCloseButton}
          >
            Back
          </BaseButton>
        )}
      </div>
    </BaseModal>
  );
};

export default FeedbackModal;

function FeedbackText({ text, value, onClick, isSelected = true, previewFeedback }) {
  return (
    <div
      className={`flex items-center ${
        isSelected || previewFeedback
          ? "bg-primary/15	border border-indigo-300 opacity-1"
          : "bg-zinc-100 border"
      }  rounded py-1 px-2 cursor-pointer hover:bg-zinc-200`}
      onClick={() => onClick(value)}
    >
      {!previewFeedback && (
        <>
          {isSelected ? (
            <i className="fa-solid fa-circle-check text-primary  mr-2"></i>
          ) : (
            <i className="fa-solid fa-plus mr-2"></i>
          )}
        </>
      )}

      <p className={`text-xs ${(isSelected || previewFeedback) && "text-primary"}`}>{text}</p>
    </div>
  );
}
